import { computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { firstValueFrom, Subject } from 'rxjs';

import {
    maximumFileAmount,
    maximumTotalSizeInBytes,
    maximumTotalSizeInFileSizeUnit,
} from '@connect-services/client/components/entity-link-document/entity-link-document.component';
import { EntityProperties } from '@connect-services/client/models/panel.model';
import { Document, DocumentService as SdkDocumentService, PermissionAliasEnum } from '@connect-services/client/sdk/api';
import { AssertService } from '@connect-services/client/services/assert.service';
import { HasPermissionsService, OperatorEnum } from '@connect-services/client/services/has-permissions.service';
import { SubscriptionService } from '@connect-services/client/services/subscription.service';
import { FileService } from '@shared/client/services/file.service';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

export type DocumentIdentifiers = {
    depositId: string;
    lost?: boolean;
    lostVersion?: number;
    technicalIdentifier?: string;
};

@Injectable({
    providedIn: 'root',
})
export class DocumentService {
    public readonly canAccessDocument = computed(
        () =>
            this.hasDocumentsSubscription() &&
            this.hasPermissionsService.checkPermissions(
                [PermissionAliasEnum.MANAGE_DOCUMENT, PermissionAliasEnum.READ_DOCUMENT],
                OperatorEnum.OR,
            ),
    );
    public readonly canManageDocuments = computed(
        () =>
            this.hasDocumentsSubscription() &&
            this.hasPermissionsService.checkPermissions([PermissionAliasEnum.MANAGE_DOCUMENT]),
    );
    public documentListChanges = new Subject<void>();
    public readonly hasDocumentsSubscription = signal(false);

    private readonly hasPermissionsService = inject(HasPermissionsService);
    private readonly sdkDocumentService = inject(SdkDocumentService);
    private readonly selectedFile: WritableSignal<File[]> = signal([]);
    private readonly snackBarService = inject(SnackBarService);
    private readonly subscriptionService = inject(SubscriptionService);

    public constructor() {
        void this.loadDocumentsSubscription();
    }

    public add(dataEntity?: EntityProperties): void {
        if (!dataEntity) {
            return;
        }

        this.linkDocument(dataEntity);
    }

    public async deleteDocument(document: Document): Promise<void> {
        await firstValueFrom(this.sdkDocumentService.documentControllerDelete({ id: document.id }));

        this.documentListChanges.next();
    }

    public async downloadAll(documentList: Document[]): Promise<void> {
        try {
            const response = await firstValueFrom(
                this.sdkDocumentService.documentControllerDownload(
                    { storagePaths: documentList.map((document: Document) => document.storagePath) },
                    'response',
                ),
            );

            FileService.downloadFile(response);
        } catch {
            this.snackBarService.openErrorSnackBar('documentDownloadFailed', { COUNT: 2 });
        }
    }

    public async downloadDocument(document: Document): Promise<void> {
        try {
            const response = await firstValueFrom(
                this.sdkDocumentService.documentControllerDownload(
                    { storagePaths: [document.storagePath] },
                    'response',
                ),
            );

            FileService.downloadFile(response);
        } catch {
            this.snackBarService.openErrorSnackBar('documentDownloadFailed', { COUNT: 1 });
        }
    }

    public async loadDocumentsSubscription(): Promise<void> {
        const subscription = await this.subscriptionService.getSubscription();

        this.hasDocumentsSubscription.set(subscription?.options.hasDocuments);
    }

    public async updateDocumentList(
        documentList: WritableSignal<Document[]>,
        data: DocumentIdentifiers,
    ): Promise<void> {
        try {
            const documents: Document[] = await firstValueFrom(
                this.sdkDocumentService.documentControllerGet({
                    depositId: data.depositId,
                    ...(typeof data.lost === 'boolean' && { lost: data.lost }),
                    ...(data.lostVersion && { lostVersion: data.lostVersion }),
                    ...(data.technicalIdentifier && { technicalIdentifier: data.technicalIdentifier }),
                }),
            );

            documentList.set(documents.sort((a: Document, b: Document) => a.storagePath.localeCompare(b.storagePath)));
        } catch {
            documentList.set([]);
        }
    }

    private linkDocument(dataEntity: EntityProperties): void {
        const inputElement = document.createElement('input');
        inputElement.type = 'file';
        inputElement.multiple = true;

        inputElement.onchange = async (event: Event): Promise<void> => {
            if (!(event.target instanceof HTMLInputElement) || !event.target.files) {
                return;
            }

            const files = Object.values(event.target.files);

            const isTotalFileSizeValid = this.validateTotalFileSize(files);
            const isNumberOfFilesValid = this.validateNumberOfFiles(files);
            if (!isTotalFileSizeValid || !isNumberOfFilesValid) {
                return;
            }

            try {
                AssertService.hasKey(dataEntity, 'technicalIdentifier');
            } catch {
                this.snackBarService.openErrorSnackBar('entityHasNoTechnicalIdentifier');

                return;
            }

            this.selectedFile.set(files);

            const importedDocuments = await firstValueFrom(
                this.sdkDocumentService.documentControllerPost({
                    files: files,
                    layerId: dataEntity.layerId,
                    technicalIdentifier: dataEntity['technicalIdentifier'],
                }),
            );

            this.snackBarService.openSuccessSnackBar('documentLinkSuccess', {
                importedDocumentsCount: this.selectedFile().length,
                linkedDocumentsCount: importedDocuments.length ?? 0,
            });

            this.documentListChanges.next();
        };

        inputElement.click();
    }

    private validateNumberOfFiles(files: File[]): boolean {
        if (files.length > maximumFileAmount) {
            this.snackBarService.openErrorSnackBar('tooManyFiles', {
                maxFileAmount: maximumFileAmount,
            });
        }

        return files.length <= maximumFileAmount;
    }

    private validateTotalFileSize(files: File[]): boolean {
        const totalSize = FileService.calculateTotalFileSize(files);
        if (totalSize > maximumTotalSizeInBytes) {
            this.snackBarService.openErrorSnackBar('totalFileSizeTooBig', {
                maxSize: maximumTotalSizeInFileSizeUnit,
                totalSize: FileService.bytesToFileSizes(totalSize),
            });
        }

        return totalSize <= maximumTotalSizeInBytes;
    }
}
